import { type ReactElement } from "react";
import type { Instrument } from "@/lib/instrument";

/** Strike depth. Bias uses 1–2 (day rule); supertrend widens to 1–4 (ST-6). */
export type Itm = 1 | 2 | 3 | 4;

interface ControlsHeaderProps {
  instrument: Instrument;
  itm: Itm;
  overrideActive: boolean;
  onOverride: (next: Instrument) => void;
}

function overrideLabel(other: Instrument, overrideActive: boolean): string {
  if (!overrideActive) return `Use ${other}`;
  const opposite: Instrument = other === "NIFTY" ? "SENSEX" : "NIFTY";
  return `Back to ${opposite}`;
}

export function ControlsHeader({
  instrument,
  itm,
  overrideActive,
  onOverride,
}: Readonly<ControlsHeaderProps>): ReactElement {
  const other: Instrument = instrument === "NIFTY" ? "SENSEX" : "NIFTY";
  const weekday = new Date().toLocaleDateString(undefined, { weekday: "long" });
  return (
    <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
      <div className="flex items-center gap-2">
        <i className="ti ti-target text-lg text-accent" />
        <h3>Trading controls</h3>
      </div>
      <div className="flex items-center gap-2">
        <span className="pill bg-accent-bg text-accent">
          <i className="ti ti-calendar text-sm" />
          {weekday} - {instrument} ITM-{String(itm)}
        </span>
        <button
          type="button"
          className="btn btn-secondary px-3 py-1 text-xs"
          onClick={() => {
            onOverride(other);
          }}
          aria-pressed={overrideActive}
          title={`Override to ${other} for today only`}
        >
          <i className="ti ti-arrows-exchange text-sm" />
          {overrideLabel(other, overrideActive)}
        </button>
      </div>
    </div>
  );
}

interface DayRuleHintProps {
  instrument: Instrument;
  itm: Itm;
  overrideActive: boolean;
}

export function DayRuleHint({
  instrument,
  itm,
  overrideActive,
}: Readonly<DayRuleHintProps>): ReactElement {
  const summary = `${instrument} ITM-${String(itm)}`;
  const text = overrideActive
    ? `Override active - using ${summary} instead of today's auto rule.`
    : `Day rule: Mon/Tue = NIFTY ITM-2, Wed/Thu = SENSEX ITM-2, Fri = NIFTY ITM-1. Today is auto-set to ${summary}; edit any field to override.`;
  return (
    <div className="mb-4 flex items-start gap-2 rounded-ctl bg-accent-bg p-2.5 text-xs text-accent">
      <i className="ti ti-info-circle text-sm" />
      <span>{text}</span>
    </div>
  );
}

interface ItmPickerProps {
  current: Itm;
  onSelect: (v: Itm) => void;
}

export function ItmPicker({ current, onSelect }: Readonly<ItmPickerProps>): ReactElement {
  return (
    <div className="mb-4">
      <div className="label">ITM offset</div>
      <div className="grid grid-cols-2 gap-2">
        <ItmOption value={1} current={current} onSelect={onSelect} />
        <ItmOption value={2} current={current} onSelect={onSelect} />
      </div>
    </div>
  );
}

interface ItmOptionProps {
  value: Itm;
  current: Itm;
  onSelect: (v: Itm) => void;
}

function ItmOption({ value, current, onSelect }: Readonly<ItmOptionProps>): ReactElement {
  const selected = value === current;
  const base = "rounded-ctl p-3 text-sm transition-colors text-center";
  const cls = selected
    ? `${base} border-2 border-accent-border bg-accent-bg text-accent font-medium`
    : `${base} border border-line text-ink-secondary hover:bg-surface-2`;
  return (
    <button
      type="button"
      className={cls}
      onClick={() => {
        onSelect(value);
      }}
    >
      ITM-{String(value)}
      <div className="mt-0.5 text-[11px] text-ink-muted">
        {value === 1 ? "1 strike in-the-money" : "2 strikes in-the-money"}
      </div>
    </button>
  );
}
